// Whether a customer may settle a booking on site ("offline") is decided by the country of their
// phone number: outside the DACH and Benelux countries an unpaid no-show cannot realistically be
// pursued. Those customers book exactly the same way, they just pay online up front.
//
// Mirrors funzone-management-backend/util/phone-payment-policy.js, which enforces the same rule
// when the booking is created (right: internalBooking:special:anyPhoneNumber) — keep the two in
// step.

export const OFFLINE_PAYMENT_COUNTRIES = [
  { callingCode: '49', iso: 'DE', name: 'Deutschland' },
  { callingCode: '43', iso: 'AT', name: 'Österreich' },
  { callingCode: '41', iso: 'CH', name: 'Schweiz' },
  { callingCode: '31', iso: 'NL', name: 'Niederlande' },
  { callingCode: '32', iso: 'BE', name: 'Belgien' },
  { callingCode: '352', iso: 'LU', name: 'Luxemburg' },
];

// Matched longest-prefix-first so Luxembourg (352) is never shadowed by a shorter entry.
const COUNTRIES_BY_PREFIX_LENGTH = [...OFFLINE_PAYMENT_COUNTRIES].sort(
  (a, b) => b.callingCode.length - a.callingCode.length
);

// A number without an international prefix is a domestic one, which is what the vast majority of
// stored numbers look like.
const DOMESTIC_COUNTRY = OFFLINE_PAYMENT_COUNTRIES[0];

export const OFFLINE_PAYMENT_BLOCKED_MESSAGE =
  'Mit dieser Telefonnummer ist nur Online-Zahlung möglich.';

// Reduce any written form to "+<digits>" (international) or "<digits>" (national).
export const normalizePhoneNumber = raw => {
  if (raw === null || raw === undefined) return '';
  // A trunk zero written as "(0)" only ever follows a country code and is not dialled.
  const value = String(raw)
    .trim()
    .replace(/([+\d])\s*\(\s*0\s*\)/, '$1');
  if (!value) return '';

  const hasInternationalPrefix = value.startsWith('+');
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';

  if (hasInternationalPrefix) return `+${digits}`;
  // "00" is the written form of "+" outside North America.
  if (digits.startsWith('00')) return `+${digits.slice(2)}`;
  return digits;
};

// Resolve the country a number belongs to, or null when it is outside the whitelist.
export const resolvePhoneCountry = raw => {
  const normalized = normalizePhoneNumber(raw);
  if (!normalized) return null;
  if (!normalized.startsWith('+')) return DOMESTIC_COUNTRY;

  const digits = normalized.slice(1);
  return COUNTRIES_BY_PREFIX_LENGTH.find(country => digits.startsWith(country.callingCode)) || null;
};

// An empty number never blocks on-site payment: requiredness is checked separately.
export const isOfflinePaymentAllowedForPhone = raw => {
  if (!normalizePhoneNumber(raw)) return true;
  return resolvePhoneCountry(raw) !== null;
};
